import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, ChevronRight, Shield, Users, MapPin, Car, Star, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import JourniLogoImg from '../assets/Journi_Logo.png';

export default function JourniLanding() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const [footerClicks, setFooterClicks] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const handleFooterClick = () => {
    if (clickTimer) clearTimeout(clickTimer);
    
    const newCount = footerClicks + 1;
    setFooterClicks(newCount);
    
    if (newCount >= 5) {
      setShowAdminModal(true);
      setFooterClicks(0);
      return;
    }
    
    const timer = setTimeout(() => setFooterClicks(0), 3000);
    setClickTimer(timer);
  };

  const handleAdminLogin = async () => {
    try {
      let apiBase = process.env.REACT_APP_API_BASE_URL;
      if (!apiBase) {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          apiBase = 'http://localhost:8010/api/v1';
        } else {
          apiBase = '/api/v1';
        }
      }
      const adminKey = process.env.REACT_APP_ADMIN_KEY || 'zGRo4rcAia7CBtI182gkXH0fm6lwnyNj';
      const res = await fetch(
        `${apiBase}/admin/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Admin-Key': adminKey
          },
          body: JSON.stringify({
            username: adminUsername,
            password: adminPassword
          })
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem('mc_token', data.token);
        let redirectUrl = `/mission-control/?token=${data.token}`;
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          redirectUrl = `http://localhost:8010/mission-control/?token=${data.token}`;
        }
        window.location.href = redirectUrl;
      } else {
        // Silent fail — close modal, show nothing
        setShowAdminModal(false);
        setAdminUsername('');
        setAdminPassword('');
      }
    } catch {
      setShowAdminModal(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: 'Is Journi free to use?',
      a: 'Yes, Journi is free to join. You get 5 free contact exchanges to connect with drivers or riders. After that, subscribe for Rs. 299/month for unlimited connections.'
    },
    {
      q: 'How is Journi different from Careem or Uber?',
      a: "Journi is a carpooling platform, not a taxi service. You share rides with real commuters going your way — not hired drivers. It's significantly cheaper and builds community among daily commuters."
    },
    {
      q: 'Is Journi safe for women?',
      a: 'Yes. Journi has female-only ride options, verified driver profiles including CNIC verification, and reliability scores. Women can filter for female drivers and female-friendly rides.'
    },
    {
      q: 'Which cities does Journi operate in?',
      a: 'Journi currently operates in Rawalpindi, Islamabad, Lahore, Karachi, Faisalabad and Sialkot — with more cities being added regularly.'
    },
    {
      q: 'How does corridor matching work?',
      a: "Unlike other apps that only match exact start and end points, Journi's smart matching finds drivers passing within 1.5km of your location — even if their route doesn't start or end at your exact address."
    },
    {
      q: 'Can I book recurring daily rides?',
      a: 'Yes. Both drivers and riders can set up recurring daily rides for their regular commute. Book once, ride every morning.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ═══ NAVIGATION ═══ */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img src={JourniLogoImg} alt="Journi — Pakistan's #1 Carpooling App" className="w-10 h-10 object-contain" />
              <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Journi
              </span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-gray-600 hover:text-emerald-600 transition">How It Works</a>
              <a href="#for-riders" className="text-gray-600 hover:text-emerald-600 transition">For Riders</a>
              <a href="#for-drivers" className="text-gray-600 hover:text-emerald-600 transition">For Drivers</a>
              <a href="#cities" className="text-gray-600 hover:text-emerald-600 transition">Cities</a>
              <a href="#safety" className="text-gray-600 hover:text-emerald-600 transition">Safety</a>
              <button 
                onClick={() => navigate('/signup')}
                className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:shadow-lg transition">
                Get Started
              </button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a href="#how-it-works" className="block text-gray-600 hover:text-emerald-600" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
              <a href="#for-riders" className="block text-gray-600 hover:text-emerald-600" onClick={() => setMobileMenuOpen(false)}>For Riders</a>
              <a href="#for-drivers" className="block text-gray-600 hover:text-emerald-600" onClick={() => setMobileMenuOpen(false)}>For Drivers</a>
              <a href="#cities" className="block text-gray-600 hover:text-emerald-600" onClick={() => setMobileMenuOpen(false)}>Cities</a>
              <a href="#safety" className="block text-gray-600 hover:text-emerald-600" onClick={() => setMobileMenuOpen(false)}>Safety</a>
              <button 
                onClick={() => navigate('/signup')}
                className="w-full px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg">
                Get Started
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ═══ SECTION 1 — HERO ═══ */}
      <section className="pt-28 pb-16 sm:pt-32 sm:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                🇵🇰 Pakistan's #1 Carpooling Platform
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Pakistan's Smartest
                <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Carpooling App
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8">
                Daily commuters across Rawalpindi, Islamabad, Lahore and Karachi share rides, split costs, and beat traffic together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:shadow-xl transition flex items-center justify-center gap-2">
                  Find a Ride
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 border-2 border-emerald-200 text-emerald-700 rounded-xl font-semibold hover:border-emerald-500 hover:bg-emerald-50 transition flex items-center justify-center gap-2">
                  <Car className="w-5 h-5" />
                  Offer a Ride
                </button>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
                <span>✓ Free to join</span>
                <span>✓ Verified drivers</span>
                <span>✓ Safe for women</span>
                <span>✓ 4.8★ rated</span>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-3xl p-8 shadow-2xl">
                <div className="bg-gradient-to-br from-emerald-200 to-teal-200 rounded-2xl h-64 flex items-center justify-center">
                  <img src={JourniLogoImg} alt="Journi carpooling app Pakistan" className="w-32 h-32 object-contain" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Safe & Verified</div>
                      <div className="text-sm text-gray-500">CNIC verified drivers</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white p-3 rounded-xl shadow-xl">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    <span className="font-bold text-gray-900">4.8</span>
                    <span className="text-sm text-gray-500">Rating</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2 — STATS BAR ═══ */}
      <section className="py-10 bg-gradient-to-r from-emerald-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            {[
              { value: '50,000+', label: 'Commuters' },
              { value: '5', label: 'Cities' },
              { value: '70%', label: 'Savings' },
              { value: '4.8★', label: 'Rating' }
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-3xl sm:text-4xl font-bold">{stat.value}</div>
                <div className="text-emerald-100 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3 — HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How Journi Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Start carpooling in 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: '📍', title: 'Enter Your Route', desc: "Tell us where you're going and when. Journi finds drivers already heading your way." },
              { num: '🤝', title: 'Get Matched', desc: "Our smart corridor matching connects you with verified commuters on your exact route — not just your destination." },
              { num: '🚗', title: 'Share & Save', desc: 'Travel together, split the cost. Save up to 70% compared to rickshaws and taxis.' }
            ].map((step, i) => (
              <div key={i} className="text-center bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4 — FOR RIDERS ═══ */}
      <section id="for-riders" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Find Daily Rides Across Pakistan
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Search rides between Rawalpindi and Islamabad, DHA to Gulberg in Lahore, DHA to Clifton in Karachi, and thousands of routes in between.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Smart corridor matching — get matched to drivers passing near you, not just your destination',
                  'Recurring daily seats — book your commute once, ride every day',
                  'Verified driver profiles — ratings, reviews, CNIC verification',
                  'Female-friendly rides — safe options for women commuters',
                  'Return trip booking — outbound and return in one request'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate('/login')}
                className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:shadow-xl transition flex items-center gap-2">
                Find Rides Near You
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8">
              <div className="space-y-4">
                {[
                  { route: 'Faizabad → Blue Area', price: 'Rs. 100', time: '8:30 AM', seats: '2 seats' },
                  { route: 'Saddar → F-8', price: 'Rs. 120', time: '9:00 AM', seats: '3 seats' },
                  { route: 'Bahria Town → I-10', price: 'Rs. 150', time: '8:00 AM', seats: '1 seat' }
                ].map((ride, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-gray-900">{ride.route}</div>
                        <div className="text-sm text-gray-500 mt-1">{ride.time} · {ride.seats}</div>
                      </div>
                      <div className="text-emerald-600 font-bold">{ride.price}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5 — FOR DRIVERS ═══ */}
      <section id="for-drivers" className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-3xl p-8 text-white">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">Rs. 40,000+</div>
                <div className="text-emerald-100 mb-6">Average monthly earnings</div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">3</div>
                    <div className="text-xs text-emerald-200">Passengers/day</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">22</div>
                    <div className="text-xs text-emerald-200">Working days</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">Rs. 600</div>
                    <div className="text-xs text-emerald-200">Daily earnings</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Turn Your Daily Commute Into Monthly Income
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                You're already driving to work every day. Journi fills your empty seats with verified commuters going your way.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Earn Rs. 30,000–50,000/month from your daily commute',
                  'Set your route once — riders come to you',
                  'Recurring passengers — same people, every morning',
                  'You control who joins — approve or decline every request',
                  'Seat bidding — riders compete for your seats, you earn more'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => navigate('/login')}
                className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:shadow-xl transition flex items-center gap-2">
                Start Offering Rides
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 6 — CITIES ═══ */}
      <section id="cities" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Carpooling Across Pakistan
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Journi connects commuters in Pakistan's busiest corridors
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { city: 'Rawalpindi ↔ Islamabad', desc: "The twin city corridor — Pakistan's most commuted route", emoji: '🏙️' },
              { city: 'Lahore', desc: 'DHA → Gulberg → MM Alam Road and beyond', emoji: '🏙️' },
              { city: 'Karachi', desc: 'DHA → Clifton → Saddar corridor', emoji: '🏙️' },
              { city: 'Faisalabad', desc: 'Susan Road → D Ground and city routes', emoji: '🏙️' },
              { city: 'Sialkot', desc: 'Cantt → City Centre daily routes', emoji: '🏙️' },
              { city: 'More Cities', desc: 'Expanding across Pakistan — your city is next', emoji: '🚀' }
            ].map((c, i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-emerald-50/50 p-6 rounded-2xl border border-gray-100 hover:shadow-lg hover:border-emerald-200 transition">
                <div className="text-3xl mb-3">{c.emoji}</div>
                <h3 className="font-bold text-gray-900 mb-1">{c.city}</h3>
                <p className="text-sm text-gray-600">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 7 — SAFETY ═══ */}
      <section id="safety" className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Built for Safety. Trusted by Commuters.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every Journi user is a real person. We verify identities, track reliability, and give you the tools to commute safely.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🛡️', title: 'Verified Profiles', desc: "CNIC verification, driver's licence checks, and vehicle registration on every driver." },
              { icon: '👩', title: 'Safe for Women', desc: 'Female-only ride options, verified female drivers, and safety ratings on every profile.' },
              { icon: '⭐', title: 'Reliability Scores', desc: 'Every driver and rider has a reliability score based on completed rides, punctuality, and reviews.' },
              { icon: '🎓', title: 'SkoolPool', desc: 'Safe, verified school runs for your children with QR check-in and parent notifications.' }
            ].map((card, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition">
                <div className="text-3xl mb-4">{card.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-sm text-gray-600">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 8 — TESTIMONIALS ═══ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Pakistani Commuters Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: "I was spending Rs. 18,000 a month on rickshaws. With Journi I spend Rs. 3,000. That's my children's school fees saved.", name: 'Faiza', role: 'Daily commuter, Rawalpindi' },
              { quote: "I drive to Islamabad every morning anyway. Journi gives me 3 passengers every day. That's Rs. 40,000 extra every month.", name: 'Ahmed', role: 'Driver, Afshan Colony' },
              { quote: "As a woman commuting alone, safety was always my concern. Journi's verified profiles and female-friendly options changed everything.", name: 'Sara', role: 'Office worker, Islamabad' }
            ].map((t, i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-emerald-50/30 p-8 rounded-2xl">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{t.quote}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{t.name}</div>
                  <div className="text-sm text-gray-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 9 — FAQ ═══ */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-emerald-50/50 transition"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  {openFaq === i ? (
                    <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 10 — FINAL CTA ═══ */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Start Your Journi Today
          </h2>
          <p className="text-lg sm:text-xl text-emerald-50 mb-8 max-w-2xl mx-auto">
            Join thousands of Pakistani commuters who are already saving money and beating traffic together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="px-8 py-4 bg-white text-emerald-600 rounded-xl font-semibold hover:shadow-2xl transition text-lg flex items-center justify-center gap-2">
              Get Started Free
              <ChevronRight className="w-5 h-5" />
            </button>
            <a 
              href="#how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition text-lg text-center">
              Learn More
            </a>
          </div>
          <p className="text-emerald-200 text-sm mt-6">
            Free to join · No credit card required · Available across Pakistan
          </p>
        </div>
      </section>

      {/* ═══ SECTION 11 — FOOTER ═══ */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4 cursor-pointer select-none" onClick={handleFooterClick}>
                <img src={JourniLogoImg} alt="Journi" className="w-8 h-8 object-contain" />
                <span className="text-xl font-bold text-white">Journi</span>
              </div>
              <p className="text-sm">
                Pakistan's smartest carpooling platform. Share rides, save money, commute smarter.
              </p>
            </div>
            
            {[
              { 
                title: 'Platform', 
                links: [
                  { label: 'Find Rides', to: '/login' },
                  { label: 'Offer Rides', to: '/login' },
                  { label: 'How It Works', href: '#how-it-works' },
                  { label: 'SkoolPool', href: '#safety' }
                ] 
              },
              {
                title: 'Cities',
                links: [
                  { label: 'Islamabad', href: '#cities' },
                  { label: 'Rawalpindi', href: '#cities' },
                  { label: 'Lahore', href: '#cities' },
                  { label: 'Karachi', href: '#cities' },
                  { label: 'Faisalabad', href: '#cities' },
                  { label: 'Sialkot', href: '#cities' }
                ]
              },
              {
                title: 'Company',
                links: [
                  { label: 'About Us', href: '#how-it-works' },
                  { label: 'Contact', href: 'https://wa.me/923312312345' }
                ]
              },
              { 
                title: 'Legal', 
                links: [
                  { label: 'Privacy Policy', to: '/privacy' },
                  { label: 'Terms of Service', to: '/terms' }
                ] 
              }
            ].map((col, i) => (
              <div key={i}>
                <h4 className="font-semibold text-white mb-4">{col.title}</h4>
                <ul className="space-y-2 text-sm">
                  {col.links.map((link, j) => (
                    <li key={j}>
                      {link.to ? (
                        <Link to={link.to} className="hover:text-emerald-400 transition">{link.label}</Link>
                      ) : (
                        <a href={link.href} className="hover:text-emerald-400 transition">{link.label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center">
            © 2026 Journi. — System architecture & product intelligence by <a href="https://byebusyness.com" target="_blank" rel="noopener noreferrer" className="text-inherit hover:underline">ByeBusyness.com</a>
          </div>
        </div>
      </footer>

      {showAdminModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setShowAdminModal(false)}
        >
          <div 
            className="bg-white rounded-lg p-8 w-80 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <input
              type="text"
              placeholder="username"
              className="w-full border rounded px-3 py-2 mb-3 text-sm text-black bg-white"
              value={adminUsername}
              onChange={e => setAdminUsername(e.target.value)}
            />
            <input
              type="password"
              placeholder="password"
              className="w-full border rounded px-3 py-2 mb-4 text-sm text-black bg-white"
              value={adminPassword}
              onChange={e => setAdminPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdminLogin()}
            />
            <button
              onClick={handleAdminLogin}
              className="w-full bg-black text-white rounded py-2 text-sm hover:bg-gray-800 transition"
            >
              Enter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}